import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../hooks/useAuth'
import { userService } from '../../services/userService'
import { supportService } from '../../services/supportService'
import catalogService from '../../services/catalogService'
import type { AdminUser } from '../../types/auth'
import type { SupportTicket, SupportTicketPriority } from '../../types/support'
import type { PaginatedResponse } from '../../types/catalog'
import { Badge } from '../../components/ui/Badge'
import {
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  TR,
  TD,
} from '../../components/ui/Table'
import { tokens } from '../../styles/tokens'

// ─── Styled ───────────────────────────────────────────────

const StatGrid = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols}, 1fr);
  gap: 16px;
  margin-bottom: 28px;
`

const StatCard = styled.div`
  background: white;
  border: 1px solid ${tokens.purple100};
  border-radius: 12px;
  padding: 20px 24px;
`

const StatValue = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 38px;
  font-weight: 300;
  color: ${tokens.purple900};
  line-height: 1;
  margin-bottom: 6px;
`

const StatLabel = styled.p`
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${tokens.purple400};
`

const StatHint = styled.p`
  font-size: 11px;
  color: ${tokens.purple200};
  margin-top: 3px;
`

const TwoCol = styled.div<{ $single: boolean }>`
  display: grid;
  grid-template-columns: ${p => (p.$single ? '1fr' : '1fr 1fr')};
  gap: 20px;
  margin-bottom: 28px;
`

const Panel = styled.div`
  background: white;
  border: 1px solid ${tokens.purple100};
  border-radius: 12px;
  overflow: hidden;
`

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid ${tokens.purple100};
`

const PanelTitle = styled.h2`
  font-size: 13px;
  font-weight: 500;
  color: ${tokens.purple900};
`

const ViewAllBtn = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: ${tokens.purple400};
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${tokens.purple700};
  }
`

const EmptyMsg = styled.div`
  padding: 32px 20px;
  text-align: center;
  font-size: 13px;
  color: ${tokens.purple200};
`

const CatalogSection = styled.div`
  margin-bottom: 12px;
`

const CatalogRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

const CatalogChip = styled.button`
  background: white;
  border: 1px solid ${tokens.purple100};
  border-radius: 10px;
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${tokens.purple400};
  }
`

const ChipValue = styled.span`
  font-family: 'Cormorant Garamond', serif;
  font-size: 24px;
  font-weight: 300;
  color: ${tokens.purple900};
  line-height: 1;
`

const ChipLabel = styled.span`
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${tokens.purple400};
`

const SkeletonBlock = styled.div<{ $w?: string; $h?: number }>`
  background: ${tokens.purple100};
  border-radius: 4px;
  height: ${p => p.$h ?? 14}px;
  width: ${p => p.$w ?? '60%'};
`

const PriorityDot = styled.span<{ $priority: SupportTicketPriority }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
  vertical-align: middle;
  background: ${({ $priority }) => {
    if ($priority === 'urgent') return '#ef4444'
    if ($priority === 'high') return '#f97316'
    if ($priority === 'medium') return '#eab308'
    return '#94a3b8'
  }};
`

const UnreadDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e8724a;
  margin-right: 6px;
  flex-shrink: 0;
`

const SubjectCell = styled.div`
  display: flex;
  align-items: center;
`

const SkeletonLoadingRows = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

// ─── Helpers ──────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) {
    return `${mins}m ago`
  }
  const hours = Math.floor(mins / 60)
  if (hours < 24) {
    return `${hours}h ago`
  }
  return `${Math.floor(hours / 24)}d ago`
}

// ─── Types ────────────────────────────────────────────────

interface CatalogCounts {
  ingredients: number
  foodGroups: number
  allergens: number
  seasons: number
  units: number
}

interface DashboardData {
  userTotal: number
  recentUsers: AdminUser[]
  openTotal: number
  unreadCount: number
  recentTickets: SupportTicket[]
  catalog: CatalogCounts
}

const INITIAL: DashboardData = {
  userTotal: 0,
  recentUsers: [],
  openTotal: 0,
  unreadCount: 0,
  recentTickets: [],
  catalog: { ingredients: 0, foodGroups: 0, allergens: 0, seasons: 0, units: 0 },
}

// ─── Component ────────────────────────────────────────────

export function DashboardPage() {
  const { token, hasRole } = useAuth()
  const navigate = useNavigate()
  const isAdmin = hasRole('ROLE_ADMIN')

  const userSvc = useMemo(() => userService(token), [token])
  const supportSvc = useMemo(() => supportService(token), [token])
  const catalogSvc = useMemo(() => catalogService(token), [token])

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<DashboardData>(INITIAL)

  useEffect(() => {
    setLoading(true)
    const none = Promise.resolve(null)
    Promise.all([
      supportSvc.list(1, 5, 'open').catch(() => null),
      supportSvc.unreadCount().catch(() => ({ count: 0 })),
      isAdmin ? userSvc.list(1, 5).catch(() => null) : none,
      isAdmin ? catalogSvc.ingredients.list(1, 1).catch(() => null) : none,
      isAdmin ? catalogSvc.foodGroups.list(1, 1).catch(() => null) : none,
      isAdmin ? catalogSvc.allergens.list(1, 1).catch(() => null) : none,
      isAdmin ? catalogSvc.seasons.list(1, 1).catch(() => null) : none,
      isAdmin ? catalogSvc.units.list(1, 1).catch(() => null) : none,
    ])
      .then(([ticketsRes, unreadRes, usersRes, ingRes, fgRes, algRes, seaRes, unitRes]) => {
        const tickets = ticketsRes as PaginatedResponse<SupportTicket> | null
        const unread = unreadRes as { count: number } | null
        const users = usersRes as PaginatedResponse<AdminUser> | null
        setData({
          userTotal: users?.total ?? 0,
          recentUsers: users?.items ?? [],
          openTotal: tickets?.total ?? 0,
          unreadCount: unread?.count ?? 0,
          recentTickets: tickets?.items ?? [],
          catalog: {
            ingredients: (ingRes as PaginatedResponse<unknown> | null)?.total ?? 0,
            foodGroups: (fgRes as PaginatedResponse<unknown> | null)?.total ?? 0,
            allergens: (algRes as PaginatedResponse<unknown> | null)?.total ?? 0,
            seasons: (seaRes as PaginatedResponse<unknown> | null)?.total ?? 0,
            units: (unitRes as PaginatedResponse<unknown> | null)?.total ?? 0,
          },
        })
      })
      .finally(() => setLoading(false))
  }, [userSvc, supportSvc, catalogSvc, isAdmin])

  // ─── Ticket panel body ──────────────────────────────────

  function renderTicketBody() {
    if (loading) {
      return (
        <SkeletonLoadingRows>
          {Array.from({ length: 5 }, (_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i}>
              <SkeletonBlock $w="70%" $h={13} style={{ marginBottom: 5 }} />
              <SkeletonBlock $w="40%" $h={10} />
            </div>
          ))}
        </SkeletonLoadingRows>
      )
    }
    if (data.recentTickets.length === 0) {
      return <EmptyMsg>No open tickets</EmptyMsg>
    }
    return (
      <TableWrapper style={{ border: 'none', borderRadius: 0 }}>
        <StyledTable>
          <THead>
            <tr>
              <TH>Subject</TH>
              <TH>Priority</TH>
              <TH>User</TH>
              <TH>When</TH>
            </tr>
          </THead>
          <tbody>
            {data.recentTickets.map(ticket => (
              <TR
                key={ticket.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/support/${ticket.id}`)}
              >
                <TD>
                  <SubjectCell>
                    {!ticket.readBySupport && <UnreadDot />}
                    <span
                      style={{
                        fontSize: 13,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 180,
                        display: 'block',
                      }}
                    >
                      {ticket.subject}
                    </span>
                  </SubjectCell>
                </TD>
                <TD>
                  <PriorityDot $priority={ticket.priority} />
                  <span style={{ fontSize: 12, textTransform: 'capitalize' }}>
                    {ticket.priority}
                  </span>
                </TD>
                <TD style={{ fontSize: 12, color: tokens.purple400 }}>
                  {ticket.user?.email ?? '—'}
                </TD>
                <TD style={{ fontSize: 12, color: tokens.purple400 }}>
                  {timeAgo(ticket.createdAt)}
                </TD>
              </TR>
            ))}
          </tbody>
        </StyledTable>
      </TableWrapper>
    )
  }

  // ─── Stats ─────────────────────────────────────────────

  const stats = [
    ...(isAdmin ? [{ label: 'Total users', value: data.userTotal, hint: null }] : []),
    { label: 'Open tickets', value: data.openTotal, hint: null },
    { label: 'Unread', value: data.unreadCount, hint: 'awaiting reply' },
    ...(isAdmin
      ? [{ label: 'Ingredients', value: data.catalog.ingredients, hint: 'in catalog' }]
      : []),
  ]

  const statCols = isAdmin ? 4 : 2

  // ─── Catalog items ──────────────────────────────────────

  const catalogItems = [
    { label: 'Ingredients', value: data.catalog.ingredients, path: '/ingredients' },
    { label: 'Food groups', value: data.catalog.foodGroups, path: '/food-groups' },
    { label: 'Allergens', value: data.catalog.allergens, path: '/allergens' },
    { label: 'Seasons', value: data.catalog.seasons, path: '/seasons' },
    { label: 'Units', value: data.catalog.units, path: '/units' },
  ]

  // ─── Render ─────────────────────────────────────────────

  return (
    <>
      <PageEyebrow>Overview</PageEyebrow>
      <PageTitle style={{ marginBottom: 28 }}>Dashboard</PageTitle>

      {/* Row 1 — Stats */}
      <StatGrid $cols={statCols}>
        {loading
          ? Array.from({ length: statCols }, (_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <StatCard key={i}>
                <SkeletonBlock $h={38} $w="40%" style={{ marginBottom: 10 }} />
                <SkeletonBlock $h={10} $w="65%" />
              </StatCard>
            ))
          : stats.map(stat => (
              <StatCard key={stat.label}>
                <StatValue>{stat.value.toLocaleString()}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
                {stat.hint && <StatHint>{stat.hint}</StatHint>}
              </StatCard>
            ))}
      </StatGrid>

      {/* Row 2 — Recent users + open tickets */}
      <TwoCol $single={!isAdmin}>
        {isAdmin && (
          <Panel>
            <PanelHead>
              <PanelTitle>Recent users</PanelTitle>
              <ViewAllBtn type="button" onClick={() => navigate('/users')}>
                View all →
              </ViewAllBtn>
            </PanelHead>
            {loading ? (
              <SkeletonLoadingRows>
                {Array.from({ length: 5 }, (_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <SkeletonBlock
                      $w="32px"
                      $h={32}
                      style={{ borderRadius: '50%', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <SkeletonBlock $w="55%" $h={12} style={{ marginBottom: 5 }} />
                      <SkeletonBlock $w="40%" $h={10} />
                    </div>
                    <SkeletonBlock $w="44px" $h={18} style={{ borderRadius: 20 }} />
                  </div>
                ))}
              </SkeletonLoadingRows>
            ) : (
              <TableWrapper style={{ border: 'none', borderRadius: 0 }}>
                <StyledTable>
                  <THead>
                    <tr>
                      <TH>User</TH>
                      <TH>Role</TH>
                      <TH>Joined</TH>
                    </tr>
                  </THead>
                  <tbody>
                    {data.recentUsers.map(user => (
                      <TR
                        key={user.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate('/users')}
                      >
                        <TD>
                          <div>
                            <span
                              style={{
                                fontSize: 13,
                                color: tokens.purple900,
                                display: 'block',
                                marginBottom: 1,
                              }}
                            >
                              {user.name ?? '—'}
                            </span>
                            <span style={{ fontSize: 11, color: tokens.purple400 }}>
                              {user.email}
                            </span>
                          </div>
                        </TD>
                        <TD>
                          <Badge color={user.isAdmin ? 'purple' : 'gray'}>
                            {user.isAdmin ? 'Admin' : 'User'}
                          </Badge>
                        </TD>
                        <TD style={{ fontSize: 12, color: tokens.purple400 }}>
                          {timeAgo(user.createdAt)}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </StyledTable>
              </TableWrapper>
            )}
          </Panel>
        )}

        <Panel>
          <PanelHead>
            <PanelTitle>Open tickets</PanelTitle>
            <ViewAllBtn type="button" onClick={() => navigate('/support')}>
              View all →
            </ViewAllBtn>
          </PanelHead>
          {renderTicketBody()}
        </Panel>
      </TwoCol>

      {/* Row 3 — Catalog at a glance (admin only) */}
      {isAdmin && (
        <CatalogSection>
          <PageEyebrow style={{ marginBottom: 12 }}>Catalog</PageEyebrow>
          <CatalogRow>
            {catalogItems.map(item => (
              <CatalogChip key={item.label} type="button" onClick={() => navigate(item.path)}>
                <ChipValue>{loading ? '—' : item.value.toLocaleString()}</ChipValue>
                <ChipLabel>{item.label}</ChipLabel>
              </CatalogChip>
            ))}
          </CatalogRow>
        </CatalogSection>
      )}
    </>
  )
}
